import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Pagination, Navigation } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import { IGif } from "../../services/types";
import { metrics, ViewRegion } from "../../utils/metrics";
import { TapTracker } from "components/UI/TapTracker";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import "./GallerySlider.scss";

interface IProps {
  gallery: IGif[];
  initialGif: IGif;
  slidePrevArrow: MutableRefObject<any>;
  slideNextArrow: MutableRefObject<any>;
  sliderPagination: MutableRefObject<HTMLDivElement | null>;
  fullscreen: boolean;
  position: number;
  source: ViewRegion;
  setCurrentGalleryGif: React.Dispatch<React.SetStateAction<IGif | null>>;
  handleLike: () => void;
}

export const GallerySlider = (props: IProps) => {
  const {
    slidePrevArrow,
    slideNextArrow,
    setCurrentGalleryGif,
    gallery,
    initialGif,
    sliderPagination,
    fullscreen,
    source,
    position,
    handleLike
  } = props;

  const [swiper, setSwiper] = useState<any>(null);
  const isSentMetrics = useRef<string[]>([]);

  const navigationSettings = {
    prevEl: slidePrevArrow.current,
    nextEl: slideNextArrow.current
  };

  useEffect(() => {
    if (swiper) {
      swiper.params.navigation.prevEl = slidePrevArrow.current;
      swiper.params.navigation.nextEl = slideNextArrow.current;

      setCurrentGalleryGif(gallery[swiper.activeIndex]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [swiper]);

  const paginationSettings = {
    el: sliderPagination.current,
    clickable: true,
    renderBullet: (index: number, className: string) => {
      return `<div class="dot ${className}"></div>`;
    },
  };

  const sendMetrics = useCallback(async (index: number) => {
    if (index === 0) {
      return;
    }

    if (!gallery[index]) {
      return;
    }

    const gif = gallery[index];

    if (isSentMetrics.current.indexOf(gif.id) === -1) {
      const loggedUser = await metrics.getUserName();

      await metrics.sendGifView({
        gifId: gif.id,
        tags: gif.tags,
        fullscreen: fullscreen,
        source: source,
        position: position,
        boosted: !!gif.cta,
        userName: loggedUser || null
      });

      isSentMetrics.current.push(gif.id);
    }
  }, [fullscreen, gallery, source, position]);

  const handleSlideChange = useCallback((swiper: any) => {
    sendMetrics(swiper.activeIndex);
    setCurrentGalleryGif(gallery[swiper.activeIndex]);
  }, [sendMetrics, gallery, setCurrentGalleryGif]);

  const initialSlide = useMemo(() => {
    const foundIndex = gallery.findIndex(({ id }) => id === initialGif.id);

    if (foundIndex === -1) {
      return 0;
    }

    return foundIndex;
  }, [initialGif, gallery]);

  const renderSlide = (item: IGif) => (
    <SwiperSlide key={item.id}>
      <img
        src={item.urls.hd ?? item.urls.sd}
        className="thumbnail"
        alt=""
        style={{
          aspectRatio: `${item.width} / ${item.height}`,
        }}
      />
    </SwiperSlide>
  );

  const renderSlides = gallery.map(renderSlide);

  const handleTapAction = useCallback((clicks: number) => {
    if (fullscreen && clicks === 2) {
      handleLike();
    }
  }, [handleLike, fullscreen]);

  return (
    <TapTracker onTap={handleTapAction}>
      <div className="GallerySlider">
        <Swiper
          slidesPerView={1}
          modules={[Navigation, Pagination]}
          navigation={navigationSettings}
          pagination={paginationSettings}
          onSwiper={setSwiper}
          onSlideChange={handleSlideChange}
          initialSlide={initialSlide}
        >
          {renderSlides}
        </Swiper>
      </div>
    </TapTracker>
  );
};
